import clinicalSession, { sameOriginEndpoint } from './clinicalSession';

export type Study = {
  register_key: string;
  patient_id: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_id?: string;
  modality_code?: string;
  bodypart_name?: string;
  register_time?: string;
  study_time?: string;
  state?: string;
};
export type StudyPage = { studies: Study[]; total: number };
export type Filters = Record<string, string | number>;
export const worklistFilterKeys = [
  'chinese_name',
  'patient_id',
  'study_id',
  'modality_code',
  'register_time_begin',
  'register_time_end',
] as const;

export function normalizeStudy(raw: Record<string, unknown>): Study {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('检查接口返回格式不正确。');
  }
  const normalized = Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [
      key.toLowerCase(),
      value == null ? '' : String(value),
    ])
  );
  if (!normalized.register_key) {
    throw new Error('检查接口缺少检查记录标识。');
  }
  if (!normalized.patient_id) {
    throw new Error('检查接口缺少患者唯一标识。');
  }
  return normalized as Study;
}
export function normalizePage(raw: any): StudyPage {
  if (!Array.isArray(raw?.Data)) {
    throw new Error('检查接口返回格式不正确。');
  }
  const total = Number(raw.totalCount ?? raw.TotalCount);
  if (!Number.isSafeInteger(total) || total < 0) {
    throw new Error('检查接口缺少有效的分页总数。');
  }
  const studies: Study[] = raw.Data.map(normalizeStudy);
  if (
    studies.length > total ||
    new Set(studies.map(study => study.register_key)).size !== studies.length
  ) {
    throw new Error('检查接口返回的记录或分页总数不一致。');
  }
  return { studies, total };
}
export function parseStudyUIDs(raw: unknown): string[] {
  if (typeof raw !== 'string') {
    throw new Error('未查询到有效的影像标识。');
  }
  const uids = [
    ...new Set(
      raw
        .split(';')
        .map(uid => uid.trim())
        .filter(Boolean)
    ),
  ];
  if (!uids.length || uids.some(uid => uid.length > 64 || !/^\d+(\.\d+)+$/.test(uid))) {
    throw new Error('未查询到有效的影像标识。');
  }
  return uids;
}
export function viewerURL(study: Study, uids: string[]): string {
  const query = new URLSearchParams({ registerKey: study.register_key });
  if (study.patient_id) {
    query.set('patientId', study.patient_id);
  }
  uids.forEach(uid => query.append('StudyInstanceUIDs', uid));
  return `/clinical/viewer?${query.toString()}`;
}
export function safeReturnPath(value: unknown): string {
  if (typeof value !== 'string') {
    return '/clinical/worklist';
  }
  // Only clinical worklist and reader are valid return destinations.
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return '/clinical/worklist';
  }
  if (
    url.origin !== window.location.origin ||
    !/^\/clinical\/(worklist|viewer(?:\/[^/]+)?)$/.test(url.pathname)
  ) {
    return '/clinical/worklist';
  }
  ['token', 'share', 'configUrl', 'customization', 'mode'].forEach(key =>
    url.searchParams.delete(key)
  );
  return `${url.pathname}${url.search}`;
}

export type Credentials = Pick<
  typeof clinicalSession,
  'ensureToken' | 'getCurrentToken' | 'getVersion' | 'clearToken'
>;
export class ClinicalApi {
  constructor(
    private credentials: Credentials = clinicalSession,
    private assertStudy: (key: string) => void = () => {},
    private cookiePolicy: RequestCredentials = 'same-origin'
  ) {}
  private configuration = {
    studiesEndpoint: '/api/clinical/reglist/stream-page',
    uidEndpoint: '/api/clinical/reglist/getSuid',
    historyEndpoint: '/api/clinical/reglist/stream-page',
    reportEndpoint: '/api/clinical/reportPicServlet',
  };
  configure(configuration = {}) {
    this.configuration = { ...this.configuration, ...configuration };
  }
  private async request(endpoint: string, body?: Filters, signal?: AbortSignal): Promise<Response> {
    const version = this.credentials.getVersion();
    const token = await this.credentials.ensureToken();
    if (this.credentials.getVersion() !== version || signal?.aborted) {
      throw new DOMException('Cancelled', 'AbortError');
    }
    const response = await fetch(sameOriginEndpoint(endpoint), {
      method: body ? 'POST' : 'GET',
      credentials: this.cookiePolicy,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (this.credentials.getVersion() !== version || signal?.aborted) {
      throw new DOMException('Cancelled', 'AbortError');
    }
    if (response.status === 401) {
      this.credentials.clearToken();
      throw new Error('医生会话已过期，请重新登录。');
    }
    if (response.status === 403) {
      throw new Error('无权访问此检查，请联系管理员。');
    }
    if (!response.ok) {
      throw new Error(response.status === 404 ? '未找到此检查或报告。' : '服务暂不可用，请重试。');
    }
    return response;
  }
  async studies(filters: Filters, page: number, signal?: AbortSignal): Promise<StudyPage> {
    const body: Filters = { currentPage: page, pageSize: 20 };
    worklistFilterKeys.forEach(key => {
      if (filters[key] !== '' && filters[key] != null) {
        body[key] =
          key === 'register_time_begin'
            ? `${filters[key]} 00:00:00`
            : key === 'register_time_end'
              ? `${new Date(new Date(`${filters[key]}T00:00:00Z`).getTime() + 86400000).toISOString().slice(0, 10)} 00:00:00`
              : filters[key];
      }
    });
    const result = normalizePage(
      await (await this.request(this.configuration.studiesEndpoint, body, signal)).json()
    );
    result.studies.forEach(study => this.assertStudy(study.register_key));
    return result;
  }
  async history(patientId: string, page: number, signal?: AbortSignal): Promise<StudyPage> {
    if (!patientId) {
      throw new Error('缺少患者唯一标识，无法查询历史检查。');
    }
    if (!this.configuration.historyEndpoint) {
      throw new Error('尚未配置历史检查接口。');
    }
    const result = normalizePage(
      await (
        await this.request(
          this.configuration.historyEndpoint,
          { patient_id: patientId, currentPage: page, pageSize: 20 },
          signal
        )
      ).json()
    );
    // Refuse cross-patient results even if the server ignores the filter.
    if (result.studies.some(study => study.patient_id !== patientId)) {
      throw new Error('历史接口返回了其他患者的记录，请联系管理员检查接口。');
    }
    result.studies.forEach(study => this.assertStudy(study.register_key));
    return result;
  }
  async uids(study: Study, signal?: AbortSignal): Promise<string[]> {
    this.assertStudy(study.register_key);
    if (!study.register_key) {
      throw new Error('缺少检查记录标识。');
    }
    const response = await this.request(
      this.configuration.uidEndpoint,
      { register_key: study.register_key },
      signal
    );
    const value = response.headers.get('Content-Type')?.includes('application/json')
      ? await response.json()
      : await response.text();
    return parseStudyUIDs(value);
  }
  async report(registerKey: string, signal?: AbortSignal): Promise<Blob> {
    this.assertStudy(registerKey);
    if (!registerKey) {
      throw new Error('请先选择检查。');
    }
    if (!this.configuration.reportEndpoint) {
      throw new Error('尚未配置只读报告接口。');
    }
    const query = new URLSearchParams({ reg_key: registerKey, output: 'pdfbyte' });
    const response = await this.request(
      `${this.configuration.reportEndpoint}?${query}`,
      undefined,
      signal
    );
    const blob = await response.blob();
    const magic = await blob.slice(0, 5).text();
    if (magic !== '%PDF-') {
      throw new Error('报告尚未生成，或接口未返回有效的 PDF 报告。');
    }
    return new Blob([blob], { type: 'application/pdf' });
  }
}
const clinicalApi = new ClinicalApi();
export default clinicalApi;
